import config from './config';
import axios from 'axios';
import logger from './utils/logger';

// Define response interface for run_garden API
export interface RaycastingResponse {
    success: boolean;
    message: string;
    session: string;
    sun_times: {
        sunrise: string;
        sunset: string;
    };
    heatmap_1d: {
        [google_place_id: string]: {
            [timestamp: string]: number;
        }
    };
    has_no_garden?: boolean;  // Flag indicating if the pub has been marked as having no garden
    garden_details?: {
        [garden_uid: string]: {
            name: string;
            is_uncertain: boolean;
        }
    };
    cached_pubs: string[];
    beer_mug_ratings?: {  // Now optional since we're calculating this in the frontend
        [google_place_id: string]: number; // Rating from 1-5
    };
    total_sun_points: {
        [google_place_id: string]: number; // Raw point-hours of sunlight
    };
    raycast_results?: any;
    garden_coords?: any;
    OFFSET_VAL?: any;
    above_horizon_steps?: number;
    meshes?: any;
    average_sun?: number[];
    warnings?: string;
    copyrights?: string[];  // Array of copyright attributions in descending order of occurrences
    garden_polygons?: {
        [garden_uid: string]: {
            polygon: [number, number][];  // Array of [lat, lon] coordinates
            elevation: number | null;  // Elevation in meters, can be null
        }
    };
}

// Interface for Garden data used by API functions
// This is a more strictly typed version of the Garden interface from types/gardenTypes
export interface Garden {
    uid: string;
    name?: string;
    garden_latlon: [number, number][] | [number, number, number][];
    elevation?: number;
    is_uncertain?: boolean;
}

// Interface for Garden sub area
export interface GardenSubArea {
    uid: string;
    sub_area_name: string;
    garden_latlon: [number, number][];
    name?: string;
    polygon?: [number, number][];
    is_uncertain?: boolean;
    elevation?: number;
    garden_size?: number;  // In sqm
}

// Cache configuration object that can be modified at runtime
export const CacheConfig = {
    // Flag to enable/disable caching globally
    // CHANGE THIS TO TRUE TO TURN ON CACHING OF RACYASTING RESULT
    enabled: true,
    
    // Flag specifically for the getPubRaycasting function
    pubRaycastingEnabled: true,
    
    // Flag specifically for the getLatLon function - disable by default
    getLatLonEnabled: false,
};

const api = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 100000,
    headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': process.env.REACT_APP_DJANGO_SECURE_API_ADMIN_KEY
    }
});

const urls = {
    run_raycasting: 'run_garden/',
    nearbyPubs: 'search_area/',
    getPubGardens: 'get_pub_sub_area_polygons/',
    getAuth: 'api-token-auth/',
    refreshToken: 'api-token-refresh/',
    getPubRaycasting: 'get_pub_raycasting/',
    getUnmoderatedPubs: 'get_unmoderated_pubs/',
    approveGarden: 'approve_garden/',
    getAppToken: 'get_app_token/', // New secure endpoint
    deleteGarden: 'delete_garden/', // Added endpoint for garden deletion
    pubByGooglePlaceId: 'pub_by_google_place_id/', // Added endpoint to search by Google Place ID
    getCoverageStats: 'get_coverage_stats/', // Added endpoint for coverage statistics
    getSunnyPubsByCity: 'get_sunny_pubs_by_city/',
    getPlaceDetails: 'get_place_details/' // Added endpoint for getting place details directly
}

export const getPubRaycasting = async (
    googlePlaceId: string,
    detailView: boolean = false,
    skipCache: boolean = false  // New optional parameter to skip cache for a specific call
) => {

    const timezone = getStoredTimezone();
    //console.log('Timezone: ', timezone);

    const params = { 
        'google_place_id': googlePlaceId,
        'detail_view': detailView.toString(),
        'timezone': timezone
    };
    //console.log(`Timezone: ${timezone}`);

    // Create a cache key for this request
    const cacheKey = `pub_raycasting_${googlePlaceId}_${detailView ? 'detail' : 'simple'}_${timezone}`;
    
    // Check if caching is enabled globally AND specifically for this function
    // Also respect the skipCache parameter for individual calls
    const useCaching = CacheConfig.enabled && CacheConfig.pubRaycastingEnabled && !skipCache;
    
    // Check if we have this result in cache (only if caching is enabled)
    const cachedResult = useCaching ? clientCache.get(cacheKey) : null;
    if (cachedResult) {
        console.log('Found cached result for pub raycasting:', googlePlaceId);
        // Make sure cached data has the required fields
        if (!('has_no_garden' in cachedResult)) {
            //logger.info(`Adding missing has_no_garden field to cached data for pub ${googlePlaceId}`);
            cachedResult.has_no_garden = false;
        }
        
        // Ensure garden_details exists in case this is an older cached response
        if (!cachedResult.garden_details && cachedResult.garden_names) {
            //logger.info(`Converting garden_names to garden_details format for pub ${googlePlaceId}`);
            cachedResult.garden_details = {};
            
            // Convert the old format to the new format
            for (const gardenId in cachedResult.garden_names) {
                cachedResult.garden_details[gardenId] = {
                    name: cachedResult.garden_names[gardenId],
                    is_uncertain: false  // Default to false for backward compatibility
                };
            }
        }
        
        // Ensure copyrights field exists, even if empty
        if (!cachedResult.copyrights) {
            //logger.info(`Adding empty copyrights array to cached data for pub ${googlePlaceId}`);
            cachedResult.copyrights = [];
        }
        
        //logger.info(`Using cached raycasting result for pub ${googlePlaceId}`);
        return { data: cachedResult };
    }
    
    // Add request deduplication to prevent multiple redundant requests
    // This is especially important in FindPubs where many requests might be made
    const requestKey = `request_${cacheKey}`;
    const pendingRequest = getPendingRequest(requestKey);
    
    if (pendingRequest) {
        //logger.info(`Reusing existing request for pub ${googlePlaceId}`);
        return pendingRequest;
    }
    
    try {
        // Store this request in pending requests
        const request = api.get(urls.getPubRaycasting, { params });
        storePendingRequest(requestKey, request);
        
        const response = await request;
        
        // Ensure copyrights field exists, even if empty
        if (response.data && !response.data.copyrights) {
            //logger.info(`Adding empty copyrights array to API response for pub ${googlePlaceId}`);
            response.data.copyrights = [];
        }
        
        // Cache successful responses (only if caching is enabled)
        if (response.data && useCaching) {
            clientCache.set(cacheKey, response.data);
            //logger.info(`Cached raycasting result for pub ${googlePlaceId}`);
        } else if (!useCaching) {
            logger.info(`Skipping cache for raycasting result for pub ${googlePlaceId} (caching disabled)`);
        }
        
        // Remove from pending requests
        clearPendingRequest(requestKey);
        
        return response;
    } catch (error) {
        // Remove from pending requests on error
        clearPendingRequest(requestKey);
        
        logger.error(`Error fetching raycasting data for pub ${googlePlaceId}:`, error);
        throw error;
    }
}

// Request deduplication system
const pendingRequests: Record<string, Promise<any>> = {};

function getPendingRequest(key: string): Promise<any> | null {
    return pendingRequests[key] || null;
}

function storePendingRequest(key: string, request: Promise<any>): void {
    pendingRequests[key] = request;
    
    // Auto-cleanup after 30 seconds in case of hanging requests
    setTimeout(() => {
        clearPendingRequest(key);
    }, 30000);
}

function clearPendingRequest(key: string): void {
    delete pendingRequests[key];
}

// Simple client cache implementation
const clientCache = {
    // Cache lifetime in milliseconds (3 hours)
    CACHE_EXPIRY: 3 * 60 * 60 * 1000,
    
    // Get item from cache
    get: (key: string) => {
        try {
            const cachedData = localStorage.getItem(`cache_${key}`);
            if (!cachedData) return null;
            
            const { timestamp, data } = JSON.parse(cachedData);
            const now = new Date().getTime();
            
            // Check if cache is expired
            if (now - timestamp > clientCache.CACHE_EXPIRY) {
                localStorage.removeItem(`cache_${key}`);
                return null;
            }
            
            return data;
        } catch (e) {
            logger.warn('Cache get failed:', e);
            return null;
        }
    },
    
    // Store item in cache
    set: (key: string, data: any) => {
        try {
            const cacheObject = {
                timestamp: new Date().getTime(),
                data: data
            };
            localStorage.setItem(`cache_${key}`, JSON.stringify(cacheObject));
        } catch (e) {
            logger.warn('Cache set failed:', e);
        }
    },
    
    // Create a consistent cache key for raycasting
    createRaycastingKey: (pubs: string[], startTime: string, endTime: string) => {
        // Sort pubs for consistent key generation regardless of order
        return `raycasting_${[...pubs].sort().join('_')}_${startTime}_${endTime}`;
    }
};

// Safe storage wrapper
const storage = {
    get: (key: string) => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                return localStorage.getItem(key);
            }
            return null;
        } catch (e) {
            logger.warn('Storage get failed:', e);
            return null;
        }
    },

    set: (key: string, value: string) => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.setItem(key, value);
                return true;
            }
            return false;
        } catch (e) {
            logger.warn('Storage set failed:', e);
            return false;
        }
    }
};

// App token management
const APP_TOKEN_KEY = 'app_token';
const APP_TOKEN_EXPIRY_KEY = 'app_token_expiry';
let memoryToken: string | null = null;
let memoryTokenExpiry: number | null = null;
let refreshInProgress = false; // Flag to track refresh status
let lastRefreshAttempt = 0; // Timestamp of last refresh attempt

export const AppAuth = {
    // Initial token request
    initializeToken: async () => {
        try {
            // Prevent multiple simultaneous initialization attempts
            if (refreshInProgress) {
                //logger.info('Token initialization already in progress, waiting...');
                // Wait for the existing refresh to complete
                await new Promise(resolve => {
                    const checkInterval = setInterval(() => {
                        if (!refreshInProgress) {
                            clearInterval(checkInterval);
                            resolve(true);
                        }
                    }, 100);
                });
                
                // If a valid token exists after waiting, return it
                if (AppAuth.getToken() && !AppAuth.isTokenExpired()) {
                    return AppAuth.getToken();
                }
            }
            
            // Set flag to prevent parallel initialization
            refreshInProgress = true;
            
            // Use the new secure endpoint that leverages the API key
            // instead of sending credentials in the request body
            const response = await axios.create({
                baseURL: config.apiBaseUrl,
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Api-Key': process.env.REACT_APP_DJANGO_SECURE_API_ADMIN_KEY
                }
            }).post(urls.getAppToken);

            const token = response.data.token;
            const expiryTime = new Date(response.data.exp).getTime();
            
            storage.set(APP_TOKEN_KEY, token);
            storage.set(APP_TOKEN_EXPIRY_KEY, expiryTime.toString());
            memoryToken = token;
            memoryTokenExpiry = expiryTime;
            lastRefreshAttempt = Date.now();
            
            return token;
        } catch (error) {
            logger.error('Failed to initialize app token:', error);
            throw error;
        } finally {
            refreshInProgress = false;
        }
    },

    // Get current token
    getToken: () => {
        return storage.get(APP_TOKEN_KEY) || memoryToken;
    },
    
    // Get token expiry time
    getTokenExpiry: () => {
        const storedExpiry = storage.get(APP_TOKEN_EXPIRY_KEY);
        return storedExpiry ? parseInt(storedExpiry) : memoryTokenExpiry;
    },
    
    // Check if token is expired or about to expire (within 5 minutes)
    isTokenExpired: () => {
        const expiryTime = AppAuth.getTokenExpiry();
        if (!expiryTime) return true;
        
        // Return true if token will expire within 5 minutes
        return Date.now() > (expiryTime - 5 * 60 * 1000);
    },
    
    // Check if we should attempt a refresh (not too soon after last attempt)
    shouldAttemptRefresh: () => {
        // Don't attempt refresh if one is already in progress
        if (refreshInProgress) return false;
        
        // Don't attempt refresh if we just tried in the last 10 seconds
        const minInterval = 10 * 1000; // 10 seconds
        return (Date.now() - lastRefreshAttempt) > minInterval;
    },

    // Refresh token
    refreshToken: async () => {
        try {
            // Prevent multiple simultaneous refresh attempts
            if (refreshInProgress) {
                //logger.info('Token refresh already in progress, waiting...');
                // Wait for the existing refresh to complete
                await new Promise(resolve => {
                    const checkInterval = setInterval(() => {
                        if (!refreshInProgress) {
                            clearInterval(checkInterval);
                            resolve(true);
                        }
                    }, 100);
                });
                
                // If a valid token exists after waiting, return it
                if (AppAuth.getToken() && !AppAuth.isTokenExpired()) {
                    return AppAuth.getToken();
                }
            }
            
            // Set flag to prevent parallel refresh attempts
            refreshInProgress = true;
            
            // Get current token and send it in the request body
            const currentToken = AppAuth.getToken();
            if (!currentToken) {
                throw new Error('No token available to refresh');
            }
            
            // Create a new instance to avoid interceptors for this special request
            const refreshInstance = axios.create({
                baseURL: config.apiBaseUrl,
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Api-Key': process.env.REACT_APP_DJANGO_SECURE_API_ADMIN_KEY,
                    // Add referrer and origin for additional server-side validation
                    'X-Client-Origin': window.location.origin,
                    'X-Client-Referer': document.referrer || window.location.href
                }
            });
            
            // Add a request timestamp to help prevent replay attacks
            const requestTimestamp = Date.now();
            
            // Send the token in the request body as required by refresh endpoint
            const response = await refreshInstance.post(urls.refreshToken, {
                token: currentToken,
                client_info: {
                    timestamp: requestTimestamp,
                    userAgent: navigator.userAgent,
                    // Include a random nonce for additional security
                    nonce: Math.random().toString(36).substring(2, 15)
                }
            });
            
            const newToken = response.data.token;
            // Calculate expiry time (token exp is in seconds since epoch)
            const expiryTime = new Date(response.data.exp).getTime();
            
            // Store token in both persistent storage and memory
            storage.set(APP_TOKEN_KEY, newToken);
            storage.set(APP_TOKEN_EXPIRY_KEY, expiryTime.toString());
            memoryToken = newToken;
            memoryTokenExpiry = expiryTime;
            lastRefreshAttempt = Date.now();
            
            return newToken;
        } catch (error) {
            logger.error('Failed to refresh token:', error);
            
            // Handle specific error codes
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 401) {
                    logger.warn('Token refresh unauthorized, attempting full re-authentication');
                    // Clear invalid token
                    storage.set(APP_TOKEN_KEY, '');
                    memoryToken = null;
                } else if (error.response.status === 403) {
                    logger.error('Access forbidden during token refresh');
                } else if (error.response.status === 429) {
                    logger.warn('Rate limit exceeded during token refresh');
                }
            }
            
            throw error;
        } finally {
            refreshInProgress = false;
        }
    }
};

// Add token to all requests with proactive token refresh
api.interceptors.request.use(
    async (config) => {
        // Skip token handling for auth-related endpoints to prevent loops
        const isAuthEndpoint = config.url && (
            config.url.includes(urls.getAppToken) ||
            config.url.includes(urls.refreshToken)
        );
        
        if (isAuthEndpoint) {
            return config;
        }
        
        // Check if token exists and is about to expire
        const hasToken = !!AppAuth.getToken();
        const isExpired = AppAuth.isTokenExpired();
        const shouldRefresh = AppAuth.shouldAttemptRefresh();
        
        if (hasToken && isExpired && shouldRefresh) {
            //logger.info('Token is expired or about to expire, refreshing...');
            try {
                // Try to refresh the token before the request
                const newToken = await AppAuth.refreshToken();
                config.headers.Authorization = `Bearer ${newToken}`;
                return config;
            } catch (refreshError) {
                logger.warn('Proactive token refresh failed, attempting to get new token');
                try {
                    // If refresh fails, try to get a completely new token
                    const newToken = await AppAuth.initializeToken();
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return config;
                } catch (initError) {
                    logger.error('Failed to initialize new token');
                    // Continue with request without token
                }
            }
        } else if (hasToken) {
            // Add existing token to request
            config.headers.Authorization = `Bearer ${AppAuth.getToken()}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Optional: Simple notification handling
// You can replace this with your preferred notification system
const notify = {
    error: (message: string) => {
        console.error(message);
        // If you have a toast/notification library, use it here
        // Example: toast.error(message);
    },
    warn: (message: string) => {
        console.warn(message);
        // Example: toast.warn(message);
    },
    info: (message: string) => {
        console.info(message);
        // Example: toast.info(message);
    }
};

// Handle token refresh on 401 errors (fallback if proactive refresh failed)
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        // Skip retry for auth-related endpoints to prevent loops
        const isAuthEndpoint = originalRequest.url && (
            originalRequest.url.includes(urls.getAppToken) ||
            originalRequest.url.includes(urls.refreshToken)
        );

        // Handle authentication errors
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint && AppAuth.shouldAttemptRefresh()) {
            originalRequest._retry = true;
            logger.info('Received 401, attempting token refresh');

            try {
                const newToken = await AppAuth.refreshToken();
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                logger.warn('Token refresh failed, attempting to get new token');
                try {
                    // If refresh fails, try to get a new token
                    const newToken = await AppAuth.initializeToken();
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);
                } catch (initError) {
                    logger.error('All authentication attempts failed');
                    
                    // Show user-friendly notification only for non-API key endpoints
                    if (!originalRequest.url?.includes('api-token-auth') && 
                        !originalRequest.url?.includes('api-token-refresh') &&
                        !originalRequest.url?.includes('get_app_token')) {
                        notify.error("Authentication failed. Please refresh the page or try again later.");
                    }
                    
                    return Promise.reject(error);
                }
            }
        }
        
        // Handle other common errors
        if (error.response?.status === 403) {
            logger.warn('Permission denied (403)');
            notify.warn("You don't have permission to perform this action.");
        } else if (error.response?.status === 404) {
            logger.warn(`Resource not found (404): ${error.config?.url}`);
        } else if (error.response?.status >= 500) {
            logger.error(`Server error (${error.response.status})`);
            notify.error("Server error occurred. Please try again later.");
        } else if (error.code === 'ECONNABORTED') {
            logger.error('Request timeout');
            notify.error("Request timed out. Please check your internet connection and try again.");
        } else if (!error.response && error.request) {
            logger.error('Network error');
            notify.error("Network error. Please check your internet connection.");
        }

        return Promise.reject(error);
    }
);

export const runRaycasting = async (
    pubs: string[],
    gardenLatLon: [string, string][][],
    startTime: string,
    endTime: string,
    rel_err_threshold: number = 13,
    show_heatmap: boolean = false,
    list_view: boolean = false,
    lowResolution: boolean = false,
): Promise<{ data: RaycastingResponse }> => {
    // Create a unique cache key for this request - using the helper method for consistency
    const cacheKey = clientCache.createRaycastingKey(pubs, startTime, endTime);
    
    // Check if we have this result in cache
    const cachedResult = clientCache.get(cacheKey);
    if (cachedResult) {
        //logger.info('Using cached raycasting result');
        
        // When returning from cache, ensure we have beer mug ratings
        // This handles cases where the cached data might be missing ratings
        if (!cachedResult.beer_mug_ratings && cachedResult.total_sun_points) {
            //logger.info('Recalculating beer mug ratings for cached result');
            
            // Create beer mug ratings if they don't exist
            cachedResult.beer_mug_ratings = {};
            
            // Calculate ratings based on total_sun_points
            for (const googlePlaceId in cachedResult.total_sun_points) {
                const pointHours = cachedResult.total_sun_points[googlePlaceId];
                // Calculate beer mug rating (similar logic as in calculateBeerMugRating)
                let rating = 1;  // Default
                
                // Simple rating calculation based on point-hours
                if (pointHours >= 5000) rating = 5;
                else if (pointHours >= 3000) rating = 4;
                else if (pointHours >= 1000) rating = 3;
                else if (pointHours >= 500) rating = 2;
                else if (pointHours >= 10) rating = 1;
                else rating = 1;
                
                cachedResult.beer_mug_ratings[googlePlaceId] = rating;
            }
            
            // Update the cache with the enhanced data
            clientCache.set(cacheKey, cachedResult);
        }
        
        // Return in the same structure as an axios response for consistency
        return { data: cachedResult };
    }

    // In case of cache miss, create a promise to track this request
    // This prevents duplicate requests for the same data during race conditions
    try {
        const data = {
            names: pubs,
            garden_latlons: gardenLatLon,
            start_time: startTime,
            end_time: endTime,
            verbose: !list_view,
            max_geometric_error: 85,
            show_lod_map: false,
            min_resolution: 15,
            rel_err_threshold: rel_err_threshold,
            garden_vertex_array_spacing: 0.5,
            show_heatmap: show_heatmap,
            cache_override: false,
            compress_response: false,
            comms_compression_threshold: 1,
            concurrent_limit: 25,
            list_view: list_view
        }

        const response = await api.post(urls.run_raycasting, data);
        
        if (response.data && response.data.success) {
            // Ensure beer mug ratings exist before caching
            if (!response.data.beer_mug_ratings && response.data.total_sun_points) {
                logger.info('Adding beer mug ratings to API response before caching');
                
                // Create beer mug ratings object
                response.data.beer_mug_ratings = {};
                
                // Calculate ratings based on total_sun_points
                for (const googlePlaceId in response.data.total_sun_points) {
                    const pointHours = response.data.total_sun_points[googlePlaceId];
                    // Calculate beer mug rating
                    let rating = 1;  // Default
                    
                    // Simple rating calculation based on point-hours
                    if (pointHours >= 5000) rating = 5;
                    else if (pointHours >= 3000) rating = 4;
                    else if (pointHours >= 1000) rating = 3;
                    else if (pointHours >= 500) rating = 2;
                    else if (pointHours >= 10) rating = 1;
                    else rating = 1;
                    
                    response.data.beer_mug_ratings[googlePlaceId] = rating;
                }
            }
            
            // Only cache successful responses
            clientCache.set(cacheKey, response.data);
        }
        
        return response;
    } catch (error) {
        logger.error('Raycasting API error:', error);
        throw error;
    }
}

// Get all SubAreas for the given Pub
export const getPubGardens = async (
    googlePlaceId: string
) => {
    if (!googlePlaceId) {
        logger.warn('Attempted to get pub data with undefined google_place_id');
        return Promise.reject(new Error('Invalid pub ID'));
    }
    
    // Create a cache key for this pub's data
    const cacheKey = `pub_polygon_${googlePlaceId}`;
    
    // Check if caching is enabled globally AND specifically for this function
    const useCaching = CacheConfig.enabled && CacheConfig.getLatLonEnabled;
    
    // Check cache first (only if caching is enabled)
    if (useCaching) {
        const cachedResult = clientCache.get(cacheKey);
        if (cachedResult) {
            //logger.info('Using cached pub polygon data');
            return { data: cachedResult };
        }
    }

    // Add request deduplication to prevent duplicate calls
    const requestKey = `request_polygon_${googlePlaceId}`;
    const pendingRequest = getPendingRequest(requestKey);
    
    if (pendingRequest) {
        //logger.info(`Reusing existing polygon request for pub ${google_place_id}`);
        return pendingRequest;
    }

    try {
        const data = {'google_place_id': googlePlaceId};
        // Store this request in pending requests
        const request = api.get(urls.getPubGardens, {params: data});
        storePendingRequest(requestKey, request);
        
        const response = await request;
        
        // Cache the successful response (only if caching is enabled)
        if (response.data && useCaching) {
            clientCache.set(cacheKey, response.data);
        }
        
        // Remove from pending requests
        clearPendingRequest(requestKey);
        
        return response;
    } catch (error) {
        // Remove from pending requests on error
        clearPendingRequest(requestKey);
        
        logger.error('GetLatLon API error:', error);
        throw error;
    }
}

// Add new searchCities function for city search
export const searchCities = async (query: string, limit: number = 10) => {
    if (!query || query.length < 2) {
        // Return empty result for short queries
        return { data: { cities: [] } };
    }
    
    try {
        // Create base URL that handles trailing slashes
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        
        const response = await api.get(`${baseUrl}search_cities/`, {
            params: { query, limit }
        });
        
        return response;
    } catch (error) {
        logger.error('Error searching cities:', error);
        throw error;
    }
};

export const getNearbyPubs = async (
    lat: number,
    lon: number,
    radius: number = 1000,
    country = "GB"
) => {
    const data = {
        lat: lat,
        lon: lon,
        radius: radius,
        max_results: 20,
        country: country
    };

    // Create a more specific cache key based on location and radius
    const cacheKey = `nearby_pubs_${lat.toFixed(4)}_${lon.toFixed(4)}_${radius}`;
    
    // Check if we have a cached result with proper expiry logic
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
        try {
            const cache = JSON.parse(cachedData);
            const timestamp = cache.timestamp;
            
            // Check if cache is still valid (less than 1 hour old)
            const now = Date.now();
            const cacheAge = now - timestamp;
            const maxCacheAge = 60 * 60 * 1000; // 1 hour
            
            if (cacheAge < maxCacheAge) {
                return { data: cache.response };
            }
        } catch (error) {
            console.error('Error parsing nearby pubs cache:', error);
        }
    }

    // Add request deduplication to prevent duplicate calls
    const requestKey = `request_nearby_pubs_${lat}_${lon}_${radius}`;
    const pendingRequest = getPendingRequest(requestKey);
    
    if (pendingRequest) {
        return pendingRequest;
    }

    try {
        // Store this request in pending requests
        const request = api.get(urls.nearbyPubs, {params: data});
        storePendingRequest(requestKey, request);
        
        const response = await request;
        
        // Cache the response with granular key
        try {
            const cacheObject = {
                timestamp: Date.now(),
                response: response.data
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheObject));
        } catch (cacheError) {
            console.error('Failed to cache nearby pubs response:', cacheError);
        }
        
        // Remove from pending requests
        clearPendingRequest(requestKey);
        
        return response;
    } catch (error) {
        // Remove from pending requests on error
        clearPendingRequest(requestKey);
        
        logger.error(`Error fetching nearby pubs: ${error}`);
        throw error;
    }
}

// Admin functions 
export const getUnmoderatedPubs = async (page: number = 1, pageSize: number = 10) => {
    try {
        const response = await api.get(urls.getUnmoderatedPubs, {
            params: {
                page,
                page_size: pageSize
            }
        });
        return response;
    } catch (error) {
        logger.error('Failed to fetch unmoderated pubs:', error);
        throw error;
    }
};

export const approveGarden = async (googlePlaceId: string) => {
    try {
        const response = await api.post(urls.approveGarden, {
            google_place_id: googlePlaceId
        });
        return response;
    } catch (error) {
        logger.error('Failed to approve garden:', error);
        throw error;
    }
};

export const fetchMinimalMeshes = async (lat: number, lon: number, max_geometric_error: number = 50, download: boolean = false) => {
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        const response = await api.get(`${baseUrl}get_minimal_meshes/`, {
            params: {
                lat,
                lon,
                offset: 5,
                max_geometric_error,
                verbose: false,
                download
            }
        });

        return response.data;
    } catch (error: any) {
        logger.error('Error fetching minimal meshes:', error);
        
        // Handle the specific flat mesh error (HTTP 422 status)
        if (error.response?.status === 422) {
            // New structured error response format
            if (error.response.data?.error_type === 'flat_mesh') {
                throw {
                    type: 'flat_mesh',
                    message: error.response.data.user_message,
                    debugInfo: error.response.data.debug_info,
                    response: error.response
                };
            }
        }
        
        // Fallback for backward compatibility using simpler regex that matches more reliably
        if (error.response?.data?.error && /flat.*triangles/.test(error.response.data.error)) {
            throw {
                type: 'flat_mesh',
                message: "We're sorry, this pub isn't currently supported by the services we use to do our calculations. Please try again somewhere else.",
                debugInfo: error.response.data.error,
                response: error.response
            };
        }
        
        // Re-throw the original error if it doesn't match our specific cases
        throw error;
    }
};

/**
 * Get the sunniest pubs for a specific city
 * @param city The city name to search for
 * @param limit Maximum number of pubs to return (default: 10)
 * @param date Optional specific date to use (format: YYYY-MM-DD)
 * @returns List of pubs sorted by sun points
 */
export const getSunnyPubsByCity = async (city: string, limit: number = 10, date?: string) => {
    try {
        const params: Record<string, string | number> = {
            city,
            limit
        };
        
        if (date) {
            params.date = date;
        }
        
        const response = await api.get(`${urls.getSunnyPubsByCity}`, { params });
        return response;
    } catch (error) {
        logger.error('Error fetching sunny pubs by city:', error);
        throw error;
    }
};

/**
 * Search for pubs by name, address, or city
 * @param query Search query (pub name, address, or city)
 * @param maxResults Maximum number of results to return
 * @returns Array of pubs matching the search query
 */
export const searchPubsByCity = async (city: string, maxResults: number = 20) => {
    try {
        // Since we no longer have searchPubs, we'll use getNearbyPubs instead
        // and filter results by city on the client side
        // Note: A more proper implementation would use Google Places API directly
        // similar to how it's done in PubSearchGoogle component
        const response = await getNearbyPubs(51.5, -0.12, 50000); // Get pubs in a large area
        
        if (response.data && response.data.pubs) {
            // Filter pubs by city (case-insensitive)
            const cityLower = city.toLowerCase();
            const filteredPubs = response.data.pubs.filter(
                (pub: any) => pub.city && pub.city.toLowerCase().includes(cityLower)
            );
            
            // Return in the same format as the original API
            return { 
                data: { 
                    pubs: filteredPubs.slice(0, maxResults) 
                } 
            };
        }
        
        return response;
    } catch (error) {
        console.error('Error searching pubs by city:', error);
        throw error;
    }
};

/**
 * Get pub details and nearby pubs in a single API call. To be used only on ViewPub page.
 * @param googlePlaceId Google Place ID to search for
 * @returns Pub details and nearby pubs if found
 */
export const getPubWithNearby = async (googlePlaceId: string) => {
    if (!googlePlaceId) {
        throw new Error('Google Place ID is required');
    }
    
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        return await api.get(`${baseUrl}get_pub_with_nearby/`, {
            params: { google_place_id: googlePlaceId }
        });
    } catch (error) {
        logger.error('Error fetching pub with nearby:', error);
        throw error;
    }
};

/**
 * Search for a pub by Google Place ID
 * @param googlePlaceId Google Place ID to search for
 * @returns Pub details if found, or null if not found
 */
export const getPubByGooglePlaceId = async (googlePlaceId: string) => {
    if (!googlePlaceId) {
        throw new Error('Google Place ID is required');
    }
    
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        const response = await axios.get(`${baseUrl}${urls.pubByGooglePlaceId}`, {
            params: { google_place_id: googlePlaceId },
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': process.env.REACT_APP_DJANGO_SECURE_API_ADMIN_KEY,
                'Authorization': `Bearer ${AppAuth.getToken()}`
            }
        });
        return response;
    } catch (error: any) {
        // If the error is 404, it means the pub doesn't exist, which is an expected case
        if (error.response && error.response.status === 404) {
            return { data: { exists: false } };
        }
        logger.error('Error fetching pub by Google Place ID:', error);
        throw error;
    }
};

/**
 * Create a new pub
 * @param pubData Object containing pub details (name, address, city, coordinates, etc.)
 * @returns API response with the created pub information
 */
export const createPub = async (pubData: any) => {
    if (!pubData || !pubData.pub) {
        throw new Error('Invalid pub data');
    }
    
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        return await api.post(`${baseUrl}create_pub/`, pubData, {
            headers: {
                'X-Api-Key': process.env.REACT_APP_DJANGO_SECURE_API_ADMIN_KEY,
                'Authorization': `Bearer ${AppAuth.getToken()}`
            }
        });
    } catch (error) {
        logger.error('Error creating pub:', error);
        throw error;
    }
};

/**
 * Delete a garden by UID
 * @param gardenUid The UID of the garden to delete
 */
export const deleteGarden = async (gardenUid: string) => {
    if (!gardenUid) {
        throw new Error('Garden UID is required');
    }
    
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        return await api.delete(`${baseUrl}${urls.deleteGarden}`, {
            data: { uid: gardenUid }
        });
    } catch (error) {
        logger.error('Error deleting garden:', error);
        throw error;
    }
};

/**
 * Toggle the uncertainty status of a garden
 * @param googlePlaceId The ID of the pub that owns the garden
 * @param garden The garden to update
 * @param isUncertain Whether the garden should be marked as uncertain
 */
export const updateGardenUncertainty = async (
    googlePlaceId: string, 
    garden: Garden | { uid: string; name?: string; garden_latlon: any[]; elevation?: number; is_uncertain?: boolean },
    isUncertain: boolean
) => {
    if (!googlePlaceId || !garden || !garden.uid) {
        throw new Error('Invalid pub ID or garden data');
    }
    
    try {
        // Prepare polygon data using existing garden data, only changing 'is_uncertain'
        // Handle different garden_latlon formats safely
        const polygonData = garden.garden_latlon.map((coord: any) => {
            // Extract first two elements regardless of array format
            return [
                Array.isArray(coord) ? coord[0] : coord.lat || coord.latitude,
                Array.isArray(coord) ? coord[1] : coord.lon || coord.longitude
            ];
        });
        
        const gardenElevation = garden.elevation || 0;
        
        const payload = {
            pub: {
                google_place_id: googlePlaceId,
                garden_drawn: true,
            },
            sub_areas: [
                {
                    uid: garden.uid,
                    name: garden.name || 'Garden',
                    polygon: polygonData,
                    elevation: gardenElevation,
                    is_uncertain: isUncertain,
                }
            ]
        };
        
        return await api.put(`${config.apiBaseUrl}pubs/${googlePlaceId}/`, payload);
    } catch (error) {
        logger.error('Error updating garden uncertainty:', error);
        throw error;
    }
};

/**
 * Mark a pub as having no garden
 * @param googlePlaceId The ID of the pub to mark
 */
export const markPubHasNoGarden = async (googlePlaceId: string) => {
    if (!googlePlaceId) {
        throw new Error('Pub ID is required');
    }
    
    try {
        const payload = {
            pub: {
                has_no_garden: true
            },
            sub_areas: [] // Send empty array to potentially clear existing gardens
        };
        
        return await api.put(`${config.apiBaseUrl}pubs/${googlePlaceId}/`, payload);
    } catch (error) {
        logger.error('Error marking pub as having no garden:', error);
        throw error;
    }
};

/**
 * Store the submitter's name in localStorage for future use
 * @param name The name of the person submitting the garden
 */
export const storeSubmitterName = (name: string): void => {
    try {
        localStorage.setItem('garden_submitter_name', name);
    } catch (error) {
        console.error('Error storing submitter name:', error);
    }
};

/**
 * Retrieve the submitter's name from localStorage
 * @returns The stored name or empty string if not found
 */
export const getSubmitterName = (): string => {
    try {
        return localStorage.getItem('garden_submitter_name') || '';
    } catch (error) {
        console.error('Error retrieving submitter name:', error);
        return '';
    }
};

/**
 * Submit garden data to the database
 * @param googlePlaceId The ID of the pub
 * @param convertedCoordinates The garden coordinates in [lat, lon, elev] format
 * @param gardenName The name of the garden
 * @param avgElevation The average elevation of the garden
 * @param isUncertain Whether the garden is uncertain
 * @param existingGardenUid The UID of an existing garden to update (optional)
 * @param submitterName The name of the person who submitted the garden
 */
export const submitGardenToDatabase = async (
    googlePlaceId: string,
    convertedCoordinates: [number, number, number][],
    gardenName: string,
    avgElevation: number,
    isUncertain: boolean = false,
    existingGardenUid?: string,
    submitterName?: string
) => {
    if (!googlePlaceId || !convertedCoordinates || convertedCoordinates.length < 3) {
        throw new Error('Invalid pub ID or garden coordinates');
    }
    
    try {
        // Prepare polygon data (Lat, Lon pairs)
        const polygonData = convertedCoordinates.map(coord => [coord[0], coord[1]]);
        
        // Construct payload
        const payload = {
            pub: {
                google_place_id: googlePlaceId,
                garden_drawn: true,
                has_no_garden: false,
            },
            sub_areas: [
                {
                    name: gardenName,
                    polygon: polygonData,
                    is_uncertain: isUncertain,
                    elevation: avgElevation,
                    submitter_name: submitterName,
                    ...(existingGardenUid && { uid: existingGardenUid })
                }
            ]
        };
        
        // Store the submitter name for future use if provided
        if (submitterName) {
            storeSubmitterName(submitterName);
        }
        
        return await api.put(`${config.apiBaseUrl}pubs/${googlePlaceId}/`, payload);
    } catch (error) {
        logger.error('Error submitting garden data:', error);
        throw error;
    }
};

// Coordinate conversion utilities moved from SubmitGarden3D

/**
 * Converts latitude, longitude, and elevation to ECEF coordinates
 * @param lat Latitude in degrees
 * @param lon Longitude in degrees
 * @param elevation Elevation in meters
 * @returns ECEF coordinates [x, y, z]
 */
export const latLonElevToECEF = (lat: number, lon: number, elevation: number = 0): [number, number, number] => {
    const latRad = (lat * Math.PI) / 180;
    const lonRad = (lon * Math.PI) / 180;
    const a = 6378137.0;
    const f = 1/298.257223563;
    const e2 = 2*f - f*f;
    const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));
    const X = (N + elevation) * Math.cos(latRad) * Math.cos(lonRad);
    const Y = (N + elevation) * Math.cos(latRad) * Math.sin(lonRad);
    const Z = (N * (1 - e2) + elevation) * Math.sin(latRad);
    return [X, Y, Z];
};

/**
 * Converts ECEF coordinates to latitude, longitude, and elevation
 * @param x X coordinate in ECEF
 * @param y Y coordinate in ECEF
 * @param z Z coordinate in ECEF
 * @returns [latitude, longitude, elevation] in degrees and meters
 */
export const ECEFToLatLonElev = (x: number, y: number, z: number): [number, number, number] => {
    const a = 6378137.0;
    const f = 1/298.257223563;
    const b = a * (1 - f);
    const e2 = 2*f - f*f;
    const lon = Math.atan2(y, x);
    const p = Math.sqrt(x*x + y*y);
    if (p < 1e-10) {
        const sign = Math.sign(z);
        return [sign * 90, 0, Math.abs(z) - b];
    }
    let lat = Math.atan2(z, p * (1 - e2));
    let N, h, prev;
    for (let i = 0; i < 5; i++) {
        prev = lat;
        const sinLat = Math.sin(lat);
        N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
        h = p / Math.cos(lat) - N;
        lat = Math.atan2(z, p * (1 - e2 * N / (N + h)));
        if (Math.abs(lat - prev) < 1e-10) break;
    }
    const sinLat = Math.sin(lat);
    N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
    h = p / Math.cos(lat) - N;
    const latDeg = (lat * 180) / Math.PI;
    const lonDeg = (lon * 180) / Math.PI;
    return [latDeg, lonDeg, h];
};

/**
 * Wrapper for ECEFToLatLonElev for compatibility
 */
export const gardenCallbackECEFToLatLonElev = (x_api: number, y_api: number, z_api: number): [number, number, number] => {
    return ECEFToLatLonElev(x_api, y_api, z_api);
};

/**
 * Check if an operation is just changing elevation
 * @param newCoordinatesECEF New ECEF coordinates
 * @param existingGarden Existing garden data
 * @returns True if only elevation has changed, false otherwise
 */
export const isElevationChangeOperation = (
    newCoordinatesECEF: [number, number, number][],
    existingGarden: GardenSubArea
): boolean => {
    // If the garden lacks coordinates or shapes are different, it's not just an elevation change
    if (!existingGarden.garden_latlon || existingGarden.garden_latlon.length !== newCoordinatesECEF.length) {
        return false;
    }
    
    try {
        const existingPolygonLatLon = existingGarden.garden_latlon;
        
        // Compare each point, ignoring elevation
        for (let i = 0; i < newCoordinatesECEF.length; i++) {
            const newPointLatLon = ECEFToLatLonElev(
                newCoordinatesECEF[i][0],
                newCoordinatesECEF[i][1],
                newCoordinatesECEF[i][2]
            );
            
            // Compare latitude and longitude with a small tolerance
            const latDiff = Math.abs(newPointLatLon[0] - existingPolygonLatLon[i][0]);
            const lonDiff = Math.abs(newPointLatLon[1] - existingPolygonLatLon[i][1]);
            
            // Define a suitable tolerance (approximately 1 meter)
            const tolerance = 0.00001;
            
            if (latDiff > tolerance || lonDiff > tolerance) {
                return false; // Points differ significantly in location
            }
        }
        
        // If all points match geographically, it's likely an elevation change
        return true;
    } catch (err) {
        console.error("Error comparing coordinates for elevation change check:", err);
        return false; // Fail safe
    }
};

/**
 * Get coverage statistics by city
 * @returns API response with city coverage stats
 */
export const getCoverageStatistics = async () => {
    try {
        const response = await api.get(urls.getCoverageStats);
        return response;
    } catch (error) {
        logger.error('Error fetching coverage statistics:', error);
        throw error;
    }
};

export interface PubYearlySunPoints {
    status: string;
google_place_id: string;
    pub_name: string;
    dates: string[];
    sun_percentages: number[];
}

export const getPubYearlySunPoints = async (googlePlaceId: string): Promise<PubYearlySunPoints> => {
    // Create a cache key for this request
    const cacheKey = `yearly_sun_points_${googlePlaceId}`;
    
    // Safety mechanism: Use a try-catch for all cache operations
    let cachedResult = null;
    try {
        // Only attempt to read from cache if caching is enabled globally
        if (CacheConfig.enabled) {
            cachedResult = clientCache.get(cacheKey);
        }
    } catch (error) {
        // Log but don't throw - we'll just proceed with the API call
        console.warn('Failed to read yearly sun data from cache, continuing with API call:', error);
    }
    
    // If we have a valid cached result, return it
    if (cachedResult) {
        return cachedResult;
    }
    
    // Otherwise make the API call
    try {
        // Use the correct endpoint URL from the Django URLconf: get_pub_yearly_sun_points
        const response = await api.get(`get_pub_yearly_sun_points/`, {
            params: { google_place_id: googlePlaceId }
        });
        
        // Attempt to cache the result, but don't block if it fails
        if (CacheConfig.enabled) {
            try {
                clientCache.set(cacheKey, response.data);
            } catch (cacheError) {
                // Just log the error - the user still gets fresh data from the API
                console.warn('Failed to cache yearly sun data, but API call succeeded:', cacheError);
            }
        }
        
        return response.data;
    } catch (error) {
        console.error('Error fetching yearly sun points:', error);
        // Return a valid but empty response structure instead of throwing
        return {
            status: 'error',
            google_place_id: googlePlaceId, // Include the requested ID
            pub_name: '', // Empty string as placeholder
            dates: [],
            sun_percentages: []
        };
    }
};

/**
 * Get pub details directly from Google Places API using place_id
 * This function uses the get_place_details endpoint which directly contacts Google
 * @param googlePlaceId Google Place ID to search for
 * @returns Pub details if found
 */
export const getPlaceDetails = async (googlePlaceId: string) => {
    if (!googlePlaceId) {
        throw new Error('Google Place ID is required');
    }
    
    // Create a cache key for this request to prevent duplicate calls
    const cacheKey = `place_details_${googlePlaceId}`;
    
    // Check if this request is already in progress
    const pendingRequest = getPendingRequest(cacheKey);
    if (pendingRequest) {
        return pendingRequest;
    }
    
    try {
        // Create the request and store it as pending
        const request = api.get(urls.getPlaceDetails, {
            params: { place_id: googlePlaceId }
        });
        
        storePendingRequest(cacheKey, request);
        
        const response = await request;
        clearPendingRequest(cacheKey);
        return response;
    } catch (error: any) {
        clearPendingRequest(cacheKey);
        
        // If the error is 404, it means the pub doesn't exist
        if (error.response && error.response.status === 404) {
            return { data: { error: 'Pub not found' } };
        }
        
        logger.error('Error fetching place details:', error);
        throw error;
    }
};

/**
 * Check if a location has 3D support for sun calculations
 * @param lat Latitude
 * @param lon Longitude
 * @returns Object with support status and source of information
 */
export const check3DSupport = async (lat: number, lon: number): Promise<{is_3d_supported: boolean | null, source: string}> => {
    try {
        const response = await api.get(`${config.apiBaseUrl}check_3d_support/`, {
            params: { lat, lon }
        });
        return response.data;
    } catch (error) {
        console.error('Error checking 3D support:', error);
        // Return null for unknown status in case of error
        return { is_3d_supported: null, source: 'error' };
    }
};

// TIMEZONE

export const getUserTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.error("Error getting timezone:", error);
      return "Europe/London"; // Fallback
    }
  };

// Store the timezone in localStorage for persistence
export const storeUserTimezone = (): void => {
    try {
      const timezone = getUserTimezone();
      localStorage.setItem('user_timezone', timezone);
    } catch (error) {
      console.error("Error storing timezone:", error);
    }
  };

export const getStoredTimezone = (): string => {
    try {
        return localStorage.getItem('user_timezone') || getUserTimezone();
    } catch (error) {
        console.error("Error retrieving timezone:", error);
        return "Europe/London"; // Fallback
    }
};


